/**
 * Auth Context
 * Contexto para gerenciar autenticação do admin
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '@/services/api';

export interface AdminUser {
  _id: string;
  email: string;
  name: string;
  role: string;
  productScopes?: string[];
}

interface AuthContextType {
  admin: AdminUser | null;
  loading: boolean;
  login: (
    email: string,
    password: string
  ) => Promise<{ requires2FA?: boolean; tempToken?: string }>;
  verify2FA: (tempToken: string, code: string, method: 'totp' | 'email') => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<boolean>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onExpired = () => {
      setAdmin(null);
      setLoading(false);
    };
    window.addEventListener('vover-admin:auth-expired', onExpired as EventListener);
    return () => window.removeEventListener('vover-admin:auth-expired', onExpired as EventListener);
  }, []);
  // Verificação inicial de autenticação:
  // 1) tenta /auth/me com adminToken
  // 2) se falhar, tenta /auth/refresh com adminRefreshToken
  // 3) sempre finaliza com loading = false
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        // Tenta primeiro usar o token de acesso atual
        const meResponse = await apiService.getMe();
        if (!isMounted) return;

        if (meResponse.data?.success) {
          setAdmin(meResponse.data.data);
          return;
        }

        // Se não tiver sucesso, cai para o fluxo de refresh
        const refreshResponse = await apiService.refreshToken();
        if (!isMounted) return;

        if (refreshResponse.data?.success) {
          const user = refreshResponse.data.user;
          if (user) {
            setAdmin(user);
            return;
          }

          // Fallback: tentar /auth/me novamente se o backend não retornou o usuário
          const meAfterRefresh = await apiService.getMe();
          if (!isMounted) return;
          if (meAfterRefresh.data?.success) {
            setAdmin(meAfterRefresh.data.data);
            return;
          }
        }

        // Se nada deu certo, não há sessão válida
        setAdmin(null);
      } catch (_err) {
        try {
          // Se /auth/me falhar (401, network, etc), tentar refresh direto
          const refreshResponse = await apiService.refreshToken();
          if (!isMounted) return;

          if (refreshResponse.data?.success) {
            const user = refreshResponse.data.user;
            if (user) {
              setAdmin(user);
              return;
            }

            const meAfterRefresh = await apiService.getMe();
            if (!isMounted) return;
            if (meAfterRefresh.data?.success) {
              setAdmin(meAfterRefresh.data.data);
              return;
            }
          }

          setAdmin(null);
        } catch {
          // Refresh também falhou: tratar como não autenticado
          if (!isMounted) return;
          setAdmin(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await apiService.login(email, password);

      if (response.data.success) {
        if (response.data.requires2FA) {
          return {
            requires2FA: true,
            tempToken: response.data.tempToken
          };
        } else {
          setAdmin(response.data.user);
          setLoading(false);
          return {};
        }
      }

      throw new Error('Credenciais inválidas');
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { message?: string } };
        message?: string;
        code?: string;
      };
      const isNetworkError =
        !ax?.response && (ax?.message === 'Network Error' || ax?.code === 'ERR_NETWORK');
      const msg = isNetworkError
        ? 'Não foi possível conectar ao servidor. Verifique se o backend está rodando (ex.: make dev-backend ou npm run dev na pasta backend).'
        : ax?.response?.data?.message ||
          (err instanceof Error ? err.message : 'Credenciais inválidas');
      throw new Error(msg);
    }
  };

  const verify2FA = async (tempToken: string, code: string, method: 'totp' | 'email') => {
    const response = await apiService.verify2FA(tempToken, code, method);

    if (response.data.success) {
      // Atualizar admin e garantir que loading seja false
      setAdmin(response.data.user);
      setLoading(false);
    } else {
      throw new Error('Código 2FA inválido');
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Ignorar erros no logout
    } finally {
      setAdmin(null);
      setLoading(false);
    }
  };

  // Função para refresh automático de token (pode ser chamada externamente)
  const refreshAuth = async (): Promise<boolean> => {
    try {
      const response = await apiService.refreshToken();
      if (response.data.success) {
        // Usar o user retornado diretamente ou fazer getMe
        if (response.data.user) {
          setAdmin(response.data.user);
          return true;
        } else {
          // Se não tiver user, tentar getMe
          const meResponse = await apiService.getMe();
          if (meResponse.data.success) {
            setAdmin(meResponse.data.data);
            return true;
          }
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        admin,
        loading,
        login,
        verify2FA,
        logout,
        refreshAuth,
        isAuthenticated: !!admin
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
